/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "hooks/useSnackbar";
import useConsent from "hooks/useConsent";
import configData from "config/config.json";
import { makeStyles } from "@mui/styles";
import {
  Button,
  Box,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FormTextInput from "components/FormTextInput";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import OperatorMultiSelect from "components/OperatorMultiSelect";
import CurrencyMultiSelect from "components/CurrencyMultiSelect";
import TimezoneMultiSelect from "components/TimezoneMultiSelect";
import { Formik, Form } from "formik";
import * as yup from "yup";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import {
  createPortalUser,
  getPortalUserInfo,
  updatePortalUser,
  deletePortalUser,
  resetPortalUserPassword,
  unlockPortalUser,
} from "services/portalUserService";
import TempPasswordDialog from "./components/TempPasswordDialog";
import PortalConfigSection from "../operator/components/PortalConfigSection";

const useStyles = makeStyles(() => ({
  pageWrapper: {
    minHeight: "100vh",
    backgroundColor: "#f5f7fa",
  },
  container: {
    padding: "24px",
    maxWidth: "1400px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
  },
  loadingWrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f7fa",
  },
  headerSection: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(102, 126, 234, 0.25)",
  },
  titleGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#ffffff",
    margin: 0,
    letterSpacing: "-0.5px",
  },
  subtitle: {
    fontSize: "14px",
    color: "rgba(255, 255, 255, 0.9)",
    margin: 0,
    fontWeight: 400,
  },
  headerActions: {
    display: "flex",
    gap: "10px",
  },
  mainContent: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px",
    flex: 1,
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  rightColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  card: {
    backgroundColor: "#ffffff",
    padding: "24px",
    borderRadius: "12px",
    border: "none",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow:
        "0 4px 12px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)",
    },
  },
  cardHeader: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#4a5568",
    marginBottom: "20px",
    paddingBottom: "12px",
    borderBottom: "2px solid #e2e8f0",
    textTransform: "uppercase",
    letterSpacing: "1px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "130px 1fr",
    gap: "16px 20px",
    alignItems: "center",
  },
  infoLabel: {
    fontSize: "13px",
    color: "#718096",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
  infoValue: {
    fontSize: "15px",
    color: "#2d3748",
    fontWeight: 500,
    padding: "8px 12px",
    backgroundColor: "#f7fafc",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  statusActive: {
    backgroundColor: "#48bb78",
    color: "#ffffff",
  },
  statusInactive: {
    backgroundColor: "#f56565",
    color: "#ffffff",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  actionCard: {
    backgroundColor: "#f7fafc",
    padding: "18px",
    borderRadius: "10px",
    border: "2px solid #e2e8f0",
    marginBottom: "12px",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#cbd5e0",
      backgroundColor: "#edf2f7",
    },
  },
  actionTitle: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#4a5568",
    marginBottom: "12px",
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  actionButtons: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  buttonPrimary: {
    minWidth: "120px",
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 600,
    padding: "8px 16px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(102, 126, 234, 0.25)",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "linear-gradient(135deg, #5568d3 0%, #6a4193 100%)",
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.35)",
      transform: "translateY(-2px)",
    },
  },
  buttonSecondary: {
    minWidth: "110px",
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 600,
    padding: "10px 20px",
    borderColor: "#cbd5e0",
    backgroundColor: "#ffffff",
    color: "#4a5568",
    borderRadius: "8px",
    borderWidth: "2px",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#a0aec0",
      backgroundColor: "#f7fafc",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    },
  },
  buttonWarning: {
    minWidth: "140px",
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 600,
    padding: "10px 20px",
    borderColor: "#f6ad55",
    backgroundColor: "#ffffff",
    color: "#ed8936",
    borderRadius: "8px",
    borderWidth: "2px",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#ed8936",
      backgroundColor: "#fffaf0",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(237, 137, 54, 0.25)",
    },
  },
  buttonDanger: {
    minWidth: "110px",
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 600,
    padding: "10px 20px",
    borderColor: "#fc8181",
    backgroundColor: "#ffffff",
    color: "#f56565",
    borderRadius: "8px",
    borderWidth: "2px",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#f56565",
      backgroundColor: "#fff5f5",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(245, 101, 101, 0.25)",
    },
  },
  createFormContainer: {
    maxWidth: "900px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  createFormGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    marginBottom: "20px",
  },
  createFormActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    height: "100%",
  },
  headerBackButton: {
    minWidth: "120px",
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 600,
    padding: "10px 24px",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: "#ffffff",
    borderRadius: "8px",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    backdropFilter: "blur(10px)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
      borderColor: "rgba(255, 255, 255, 0.5)",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    },
  },
  dangerZoneCard: {
    border: "2px solid #fed7d7",
    backgroundColor: "#fffaf0",
  },
  iconWrapper: {
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    color: "#ffffff",
    fontWeight: 700,
  },
  stickyBar: {
    position: "sticky",
    bottom: 0,
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "12px 24px",
    backgroundColor: "#ffffff",
    borderTop: "1px solid #e2e8f0",
    boxShadow: "0 -4px 16px rgba(0, 0, 0, 0.08)",
    marginTop: "24px",
  },
}));

const createValidationSchema = yup.object().shape({
  name: yup
    .string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters"),
  email: yup
    .string()
    .email("Email must be valid")
    .required("Email is required"),
  operatorIds: yup
    .array()
    .of(yup.string())
    .min(1, "At least one operator is required"),
});

const editValidationSchema = yup.object().shape({
  name: yup
    .string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters"),
  operatorIds: yup
    .array()
    .of(yup.string())
    .min(1, "At least one operator is required"),
});

const PortalUserEditor = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSnackbar } = useSnackbar();
  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [tempPassword, setTempPassword] = useState("");
  const [showTempPasswordDialog, setShowTempPasswordDialog] = useState(false);
  const [populatedOperators, setPopulatedOperators] = useState<any>({});
  const [portalConfig, setPortalConfig] = useState<any>({});

  const isCreate = !id;

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.PORTAL_USER?.DELETE_ROLES,
  );
  const hasResetPasswordPermission = sessionHelper.hasRole(
    PermittedRoles.PORTAL_USER?.RESET_PASSWORD_ROLES,
  );
  const hasUnlockPermission = sessionHelper.hasRole(
    PermittedRoles.PORTAL_USER?.UNLOCK_ROLES,
  );

  const isAccountLocked = (u: any) =>
    u?.lockedUntil && new Date() < new Date(u.lockedUntil);

  useEffect(() => {
    if (id) {
      fetchUserData(id);
    }
  }, [id]);

  const fetchUserData = async (userId: string) => {
    try {
      setLoading(true);
      const response = await getPortalUserInfo(userId);
      if (response?.status && response?.data) {
        setUser(response.data);
        setPortalConfig(response.data.portalConfig || {});
        // Build populatedOperators map for OperatorMultiSelect
        const opMap: any = {};
        response.data.operatorIds?.forEach((op: any) => {
          opMap[op._id] = op;
        });
        setPopulatedOperators(opMap);
      }
    } catch (error: any) {
      console.error("Error fetching portal user:", error);
      showSnackbar(
        error?.message || "Failed to fetch portal user data",
        "error",
      );
      navigate((configData.routes as any).portalUsers.list);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      setLoading(true);

      const response = await createPortalUser({
        name: values.name,
        email: values.email,
        operatorIds: values.operatorIds,
        currencies: values.currencies,
        timezones: values.timezones,
        ...(Object.keys(portalConfig).length > 0 && { portalConfig }),
      });

      if (response?.status) {
        // Show temp password dialog if a temporary password was returned
        if (response.data?.tempPassword) {
          setTempPassword(response.data.tempPassword);
          setShowTempPasswordDialog(true);
        } else {
          showSnackbar("Portal user created successfully", "success");
          navigate((configData.routes as any).portalUsers.list);
        }
      }
    } catch (error: any) {
      console.error("Error creating portal user:", error);
      showSnackbar(error?.message || "Failed to create portal user", "error");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleTempPasswordDialogClose = () => {
    setShowTempPasswordDialog(false);
    setTempPassword("");
    showSnackbar("Portal user created successfully", "success");
    navigate((configData.routes as any).portalUsers.list);
  };

  // Handle name update
  const handleUpdateUser = async (values: any) => {
    try {
      setLoading(true);

      const response = await updatePortalUser(id!, {
        name: values.name,
        operatorIds: values.operatorIds,
        currencies: values.currencies,
        timezones: values.timezones,
        portalConfig,
      });
      if (response?.status) {
        showSnackbar("Portal user updated successfully", "success");
        await fetchUserData(id!);
      }
    } catch (error: any) {
      console.error("Error updating portal user:", error);
      showSnackbar(error?.message || "Failed to update portal user", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle status toggle
  const handleStatusUpdate = (newStatus: boolean) => {
    updateDetails({
      itemName: user?.name,
      message: `Are you sure you want to ${newStatus ? "activate" : "deactivate"} portal user`,
      btnText: newStatus ? "Activate" : "Deactivate",
      onConfirm: () => confirmStatusUpdate(newStatus),
    });
    openConsent();
  };

  const confirmStatusUpdate = async (newStatus: boolean) => {
    closeConsent();
    try {
      setLoading(true);
      const response = await updatePortalUser(id!, { isActive: newStatus });
      if (response?.status) {
        showSnackbar("Portal user status updated successfully", "success");
        await fetchUserData(id!);
      }
    } catch (error: any) {
      console.error("Error updating status:", error);
      showSnackbar(error?.message || "Failed to update status", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset
  const handleResetPassword = () => {
    updateDetails({
      itemName: user?.name,
      message: "Are you sure you want to reset the password for",
      btnText: "Reset Password",
      onConfirm: confirmResetPassword,
    });
    openConsent();
  };

  const confirmResetPassword = async () => {
    closeConsent();
    try {
      setLoading(true);
      const response = await resetPortalUserPassword(id!);
      if (response?.status) {
        if (response.data?.tempPassword) {
          setTempPassword(response.data.tempPassword);
          setShowTempPasswordDialog(true);
        } else {
          showSnackbar("Password reset successfully", "success");
        }
        await fetchUserData(id!);
      }
    } catch (error: any) {
      console.error("Error resetting password:", error);
      showSnackbar(error?.message || "Failed to reset password", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle unlock account
  const handleUnlockAccount = () => {
    updateDetails({
      itemName: user?.name,
      message: "Are you sure you want to unlock the account for",
      btnText: "Unlock Account",
      onConfirm: confirmUnlockAccount,
    });
    openConsent();
  };

  const confirmUnlockAccount = async () => {
    closeConsent();
    try {
      setLoading(true);
      const response = await unlockPortalUser(id!);
      if (response?.status) {
        showSnackbar("Account unlocked successfully", "success");
        await fetchUserData(id!);
      }
    } catch (error: any) {
      console.error("Error unlocking account:", error);
      showSnackbar(error?.message || "Failed to unlock account", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle deactivate (delete)
  const handleDeactivateUser = () => {
    updateDetails({
      itemName: user?.name,
      message: "Are you sure you want to deactivate portal user",
      btnText: "Deactivate",
      onConfirm: confirmDeactivateUser,
    });
    openConsent();
  };

  const confirmDeactivateUser = async () => {
    closeConsent();
    try {
      setLoading(true);
      const response = await deletePortalUser(id!);
      if (response?.status) {
        showSnackbar("Portal user deactivated successfully", "success");
        navigate((configData.routes as any).portalUsers.list);
      }
    } catch (error: any) {
      console.error("Error deactivating portal user:", error);
      showSnackbar(
        error?.message || "Failed to deactivate portal user",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate((configData.routes as any).portalUsers.list);
  };

  const initialValues = isCreate
    ? {
        name: "",
        email: "",
        operatorIds: [] as string[],
        currencies: [] as string[],
        timezones: [] as string[],
      }
    : {
        name: user?.name || "",
        email: user?.email || "",
        operatorIds:
          user?.operatorIds?.map((op: any) =>
            typeof op === "string" ? op : op._id,
          ) || [],
        currencies: user?.currencies || ([] as string[]),
        timezones: user?.timezones || ([] as string[]),
      };

  return (
    <>
      {loading ? (
        <div className={styles.loadingWrapper}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress />
          </Box>
        </div>
      ) : (
        <div className={styles.pageWrapper}>
          <div className={styles.container}>
            {/* Header */}
            <div className={styles.headerSection}>
              <div className={styles.titleGroup}>
                <h1 className={styles.title}>
                  {isCreate
                    ? "Create Portal User"
                    : `Edit Portal User: ${user?.name || ""}`}
                </h1>
                <p className={styles.subtitle}>
                  {isCreate
                    ? "Add a new portal user to the system"
                    : user?.email || ""}
                </p>
              </div>
              <div className={styles.headerActions}>
                <Button
                  onClick={handleCancel}
                  variant="outlined"
                  size="medium"
                  className={styles.headerBackButton}
                >
                  {isCreate ? "Cancel" : "Back to List"}
                </Button>
              </div>
            </div>

            {/* Create Form */}
            {isCreate ? (
              <div className={styles.createFormContainer}>
                <Formik
                  initialValues={initialValues}
                  validationSchema={createValidationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    isSubmitting,
                  }) => (
                    <Form>
                      <div className={styles.card}>
                        <div className={styles.cardHeader}>
                          <span className={styles.iconWrapper}>+</span>
                          New Portal User
                        </div>
                        <div className={styles.form}>
                          <div className={styles.createFormGrid}>
                            <FormTextInput
                              label="Name"
                              name="name"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.name && Boolean(errors.name)}
                              helperText={touched.name && errors.name}
                            />
                            <FormTextInput
                              label="Email"
                              name="email"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.email && Boolean(errors.email)}
                              helperText={touched.email && errors.email}
                            />
                          </div>
                          <OperatorMultiSelect
                            label="Operators"
                            value={values.operatorIds}
                            onChange={(ids: string[]) =>
                              setFieldValue("operatorIds", ids)
                            }
                            error={
                              touched.operatorIds && Boolean(errors.operatorIds)
                            }
                            helperText={
                              touched.operatorIds
                                ? (errors.operatorIds as string)
                                : undefined
                            }
                          />
                          <Box>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              mb={0.5}
                            >
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#718096",
                                  fontWeight: 600,
                                }}
                              >
                                Currencies
                              </span>
                              <Tooltip
                                title="The first selected currency in the list will be the default currency for this user."
                                placement="top"
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  sx={{ padding: "2px" }}
                                  aria-label="currency info"
                                >
                                  <InfoOutlinedIcon
                                    sx={{
                                      fontSize: "14px",
                                      color: "#a0aec0",
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <CurrencyMultiSelect
                              value={values.currencies}
                              onChange={(currencies) =>
                                setFieldValue("currencies", currencies)
                              }
                              error={
                                touched.currencies && Boolean(errors.currencies)
                              }
                              helperText={
                                touched.currencies
                                  ? (errors.currencies as string)
                                  : undefined
                              }
                            />
                          </Box>
                          <Box>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              mb={0.5}
                            >
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#718096",
                                  fontWeight: 600,
                                }}
                              >
                                Timezones
                              </span>
                              <Tooltip
                                title="The first selected timezone will be the default timezone for this user in the portal."
                                placement="top"
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  sx={{ padding: "2px" }}
                                  aria-label="timezone info"
                                >
                                  <InfoOutlinedIcon
                                    sx={{
                                      fontSize: "14px",
                                      color: "#a0aec0",
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <TimezoneMultiSelect
                              value={values.timezones}
                              onChange={(timezones) =>
                                setFieldValue("timezones", timezones)
                              }
                            />
                          </Box>
                        </div>
                      </div>

                      <PortalConfigSection
                        portalConfig={portalConfig}
                        onChange={(e: any) => setPortalConfig(e.target.value)}
                        onSave={null}
                        loading={loading}
                        hideSaveButton={true}
                      />

                      <div className={styles.stickyBar}>
                        <Button
                          variant="outlined"
                          onClick={handleCancel}
                          className={styles.buttonSecondary}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={isSubmitting || loading}
                          className={styles.buttonPrimary}
                        >
                          {isSubmitting ? "Creating..." : "Create Portal User"}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            ) : (
              /* Edit View */
              user && (
                <Formik
                  initialValues={initialValues}
                  validationSchema={editValidationSchema}
                  onSubmit={handleUpdateUser}
                  enableReinitialize
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    isSubmitting,
                  }) => (
                    <Form>
                      <div className={styles.mainContent}>
                        {/* Left Column - User Info & Edit Form */}
                        <div className={styles.leftColumn}>
                          {/* User Info Card */}
                          <div className={styles.card}>
                            <div className={styles.cardHeader}>
                              <span className={styles.iconWrapper}>i</span>
                              User Information
                            </div>
                            <div className={styles.infoGrid}>
                              <span className={styles.infoLabel}>Email</span>
                              <span className={styles.infoValue}>
                                {user.email}
                              </span>
                              <span className={styles.infoLabel}>Status</span>
                              <span>
                                <span
                                  className={`${styles.statusBadge} ${
                                    user.isActive
                                      ? styles.statusActive
                                      : styles.statusInactive
                                  }`}
                                >
                                  {user.isActive ? "Active" : "Inactive"}
                                </span>
                              </span>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <span className={styles.infoLabel}>
                                  Lock Status
                                </span>
                                <Tooltip
                                  title="An account is automatically locked after 5 consecutive failed login attempts. It unlocks automatically after 15 minutes, or an admin can unlock it manually."
                                  placement="top"
                                  arrow
                                >
                                  <IconButton
                                    size="small"
                                    sx={{ padding: "2px" }}
                                    aria-label="lock status info"
                                  >
                                    <InfoOutlinedIcon
                                      sx={{
                                        fontSize: "14px",
                                        color: "#a0aec0",
                                      }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <span>
                                {isAccountLocked(user) ? (
                                  <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={0.5}
                                  >
                                    <span
                                      className={styles.statusBadge}
                                      style={{
                                        backgroundColor: "#e53e3e",
                                        color: "white",
                                      }}
                                    >
                                      Locked
                                    </span>
                                    <span
                                      style={{
                                        fontSize: "12px",
                                        color: "#718096",
                                      }}
                                    >
                                      Until{" "}
                                      {new Date(
                                        user.lockedUntil,
                                      ).toLocaleString()}
                                    </span>
                                  </Box>
                                ) : (
                                  <span
                                    className={styles.statusBadge}
                                    style={{
                                      backgroundColor: "#48bb78",
                                      color: "white",
                                    }}
                                  >
                                    Unlocked
                                  </span>
                                )}
                              </span>
                              <span className={styles.infoLabel}>
                                Password Reset
                              </span>
                              <span className={styles.infoValue}>
                                {user.requiresPasswordReset
                                  ? "Required"
                                  : "Not Required"}
                              </span>
                            </div>
                          </div>

                          {/* Edit Form Card */}
                          <div className={styles.card}>
                            <div className={styles.cardHeader}>
                              <span className={styles.iconWrapper}>E</span>
                              Edit Details
                            </div>
                            <div className={styles.form}>
                              <FormTextInput
                                label="Name"
                                name="name"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                              />
                              <OperatorMultiSelect
                                label="Operators"
                                value={values.operatorIds}
                                onChange={(ids: string[]) =>
                                  setFieldValue("operatorIds", ids)
                                }
                                error={
                                  touched.operatorIds &&
                                  Boolean(errors.operatorIds)
                                }
                                helperText={
                                  touched.operatorIds
                                    ? (errors.operatorIds as string)
                                    : undefined
                                }
                                populatedOperators={populatedOperators}
                              />
                              <Box>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                  mb={0.5}
                                >
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      color: "#718096",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Currencies
                                  </span>
                                  <Tooltip
                                    title="The first selected currency in the list will be the default currency for this user."
                                    placement="top"
                                    arrow
                                  >
                                    <IconButton
                                      size="small"
                                      sx={{ padding: "2px" }}
                                      aria-label="currency info"
                                    >
                                      <InfoOutlinedIcon
                                        sx={{
                                          fontSize: "14px",
                                          color: "#a0aec0",
                                        }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <CurrencyMultiSelect
                                  value={values.currencies}
                                  onChange={(currencies) =>
                                    setFieldValue("currencies", currencies)
                                  }
                                  error={
                                    touched.currencies &&
                                    Boolean(errors.currencies)
                                  }
                                  helperText={
                                    touched.currencies
                                      ? (errors.currencies as string)
                                      : undefined
                                  }
                                />
                              </Box>
                              <Box>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                  mb={0.5}
                                >
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      color: "#718096",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Timezones
                                  </span>
                                  <Tooltip
                                    title="The first selected timezone will be the default timezone for this user in the portal."
                                    placement="top"
                                    arrow
                                  >
                                    <IconButton
                                      size="small"
                                      sx={{ padding: "2px" }}
                                      aria-label="timezone info"
                                    >
                                      <InfoOutlinedIcon
                                        sx={{
                                          fontSize: "14px",
                                          color: "#a0aec0",
                                        }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <TimezoneMultiSelect
                                  value={values.timezones}
                                  onChange={(timezones) =>
                                    setFieldValue("timezones", timezones)
                                  }
                                />
                              </Box>
                            </div>
                          </div>
                        </div>

                        {/* Right Column - Actions */}
                        <div className={styles.rightColumn}>
                          <div className={styles.card}>
                            <div className={styles.cardHeader}>
                              <span className={styles.iconWrapper}>A</span>
                              Actions
                            </div>

                            {/* Status Toggle */}
                            <div className={styles.actionCard}>
                              <div className={styles.actionTitle}>
                                Status Management
                              </div>
                              <div className={styles.actionButtons}>
                                {user.isActive ? (
                                  <Button
                                    variant="outlined"
                                    className={styles.buttonWarning}
                                    onClick={() => handleStatusUpdate(false)}
                                    disabled={loading}
                                  >
                                    Deactivate User
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outlined"
                                    className={styles.buttonSecondary}
                                    onClick={() => handleStatusUpdate(true)}
                                    disabled={loading}
                                  >
                                    Activate User
                                  </Button>
                                )}
                              </div>
                            </div>

                            {/* Password Reset */}
                            {hasResetPasswordPermission && (
                              <div className={styles.actionCard}>
                                <div className={styles.actionTitle}>
                                  Password Reset
                                </div>
                                <div className={styles.actionButtons}>
                                  <Button
                                    variant="outlined"
                                    className={styles.buttonWarning}
                                    onClick={handleResetPassword}
                                    disabled={loading}
                                  >
                                    Reset Password
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* Unlock Account */}
                            {hasUnlockPermission && isAccountLocked(user) && (
                              <div className={styles.actionCard}>
                                <div className={styles.actionTitle}>
                                  Account Locked
                                </div>
                                <div className={styles.actionButtons}>
                                  <Button
                                    variant="outlined"
                                    className={styles.buttonSecondary}
                                    onClick={handleUnlockAccount}
                                    disabled={loading}
                                  >
                                    Unlock Account
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* Danger Zone */}
                            {hasDeletePermission && (
                              <div
                                className={`${styles.actionCard} ${styles.dangerZoneCard}`}
                              >
                                <div className={styles.actionTitle}>
                                  Danger Zone
                                </div>
                                <div className={styles.actionButtons}>
                                  <Button
                                    variant="outlined"
                                    className={styles.buttonDanger}
                                    onClick={handleDeactivateUser}
                                    disabled={loading || !user.isActive}
                                  >
                                    Deactivate & Disable
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Portal Config - full width */}
                      <Box mt={3}>
                        <PortalConfigSection
                          portalConfig={portalConfig}
                          onChange={(e: any) => setPortalConfig(e.target.value)}
                          onSave={null}
                          loading={loading}
                          hideSaveButton={true}
                        />
                      </Box>

                      {/* Sticky Save Bar */}
                      <div className={styles.stickyBar}>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={isSubmitting || loading}
                          className={styles.buttonPrimary}
                        >
                          {isSubmitting ? "Saving..." : "Save Changes"}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              )
            )}
          </div>
        </div>
      )}

      {/* Consent Dialog */}
      <DeleteConsentDialog
        open={isOpenConsent}
        itemName={constentDetails?.itemName}
        message={constentDetails?.message}
        btnText={constentDetails?.btnText}
        onClose={closeConsent}
        onConfirm={constentDetails?.onConfirm}
      />

      {/* Temp Password Dialog */}
      <TempPasswordDialog
        open={showTempPasswordDialog}
        tempPassword={tempPassword}
        onClose={handleTempPasswordDialogClose}
      />
    </>
  );
};

export default PortalUserEditor;
